import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { AlertCircle, Lock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';

// Configuração de acesso
const DEFAULT_USERNAME = 'admin';
const DEFAULT_PASSWORD = 'docs2023';

// Chave para armazenar o estado de autenticação no localStorage
const AUTH_KEY = 'ai-visor-docs-auth';

interface AccessValidationProps {
  children: React.ReactNode;
}

const AccessValidation = ({ children }: AccessValidationProps) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar se já está autenticado (via localStorage)
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = localStorage.getItem(AUTH_KEY);
      if (authStatus === 'true') {
        setIsAuthenticated(true);
      }
      setIsLoading(false);
    };

    // Pequeno delay para evitar flash de conteúdo
    const timer = setTimeout(checkAuth, 300);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validação de acesso
    if (username === DEFAULT_USERNAME && password === DEFAULT_PASSWORD) {
      setIsAuthenticated(true);
      localStorage.setItem(AUTH_KEY, 'true');
      setError('');
    } else {
      setError('Usuário ou senha incorretos');
      localStorage.removeItem(AUTH_KEY);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem(AUTH_KEY);
    setUsername('');
    setPassword('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
        <Card className="w-full max-w-md p-6">
          <div className="flex flex-col space-y-6">
            <div className="flex flex-col items-center text-center space-y-2">
              <Lock className="h-12 w-12 text-primary" />
              <h1 className="text-2xl font-bold">Acesso à Documentação</h1>
              <p className="text-sm text-muted-foreground">
                Esta área é restrita. Por favor, digite suas credenciais para continuar.
              </p>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Erro</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="username" className="text-sm font-medium">
                  Usuário
                </label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Digite seu usuário"
                  required
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  Senha
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite sua senha"
                  required
                />
              </div>

              <Button type="submit" className="w-full">
                Acessar Documentação
              </Button>
            </form>

            <div className="text-xs text-center text-muted-foreground">
              <p>Credenciais padrão: admin / docs2023</p>
              <p>Você pode alterar estas credenciais no componente AccessValidation.tsx</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative">
      <Button 
        variant="outline" 
        size="sm" 
        className="absolute top-4 right-4 z-10"
        onClick={handleLogout}
      >
        Sair
      </Button>
      {children}
    </div>
  );
};

export default AccessValidation;
